import { z } from 'zod'
import { GPS_REFUSAL_REASONS, LOCATION_LEVELS, LocationPolicySchema } from '../domain/location-policy'

/** A page-side spoof hint code (`non_native_geolocation`): a short snake_case token, never free text. */
export const SuspectHintSchema = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/)

/**
 * Location evidence v2 (docs/specs/2026-10-26-location-v2-design.md "Evaluation"): the shape `evaluateLocation`
 * returns (in memory; `members.location` stored it as `version: 2` before the identity-document spec §8.7, which
 * stores the codes-only LocationVerdict instead), and the device-fix details the admin sees.
 */

export const VERDICTS = ['match', 'mismatch', 'inconclusive'] as const
export const VerdictSchema = z.enum(VERDICTS)
export type Verdict = z.infer<typeof VerdictSchema>

export const NETWORK_TYPES = ['fixed', 'mobile', 'relay', 'unknown'] as const
export type NetworkType = (typeof NETWORK_TYPES)[number]

/** Why a verdict came out as it did (rendered in words on the admin page). */
export const DEVICE_WHY = [
  'near_place',
  'within_radius',
  'same_region',
  'same_country',
  'outside_radius',
  'other_region',
  'other_country',
  'not_precise_enough',
  'region_unknown',
  'no_place_nearby',
  'stale',
  'missing',
  'refused',
  'other_place',
] as const
export type DeviceWhy = (typeof DEVICE_WHY)[number]

export const DeviceDiagnosticsSchema = z.object({
  samples: z.array(
    z.object({ accuracyM: z.number(), ageMs: z.number(), hasAltitude: z.boolean(), hasSpeed: z.boolean() })
  ),
  firstAccuracyM: z.number().nullable(),
  bestAccuracyM: z.number().nullable(),
  elapsedMs: z.number().nullable(),
  ageMs: z.number().nullable(),
  stale: z.boolean(),
  /** phone | tablet | desktop | unknown, from the user agent. */
  uaClass: z.string(),
  /** Soft spoof signals (`constant_samples`, `non_native_geolocation`): shown, never a reason. */
  suspect: z.array(SuspectHintSchema).max(5),
})
export type DeviceDiagnostics = z.infer<typeof DeviceDiagnosticsSchema>

/** What the fix-time measurement keeps from the exact point (the point itself is discarded). */
export const DeviceMeasureSchema = z.object({
  placeId: z.number().int(),
  /** |P − D| in km, one decimal. */
  distanceKm: z.number(),
  accuracyM: z.number(),
  ageMs: z.number(),
  stale: z.boolean(),
  /** The nearest `places` row to the exact point within 50 km. */
  nearest: z.object({ country: z.string(), admin1: z.string().nullable(), km: z.number() }).nullable(),
})
export type DeviceMeasure = z.infer<typeof DeviceMeasureSchema>

export const DeviceEvaluationSchema = z.object({
  /** `prior`: no device answer left (retention removed it); the verdict is the one stored with the previous decision. */
  state: z.enum(['fix', 'refused', 'none', 'other_place', 'prior']),
  verdict: z.enum([...VERDICTS, 'missing']),
  why: z.enum(DEVICE_WHY),
  distanceKm: z.number().nullable(),
  accuracyM: z.number().nullable(),
  ageMs: z.number().nullable(),
  nearest: DeviceMeasureSchema.shape.nearest,
  refusal: z.enum(GPS_REFUSAL_REASONS).nullable(),
  timeZone: z.string().nullable(),
  diagnostics: DeviceDiagnosticsSchema.nullable(),
  /** The fix-time measurement (in memory only; never stored on the member row, identity-document spec §8.7). */
  measure: DeviceMeasureSchema.nullable(),
})
export type DeviceEvaluation = z.infer<typeof DeviceEvaluationSchema>

export const IpSourceEvaluationSchema = z.object({
  ok: z.boolean(),
  error: z.string().nullable(),
  countryIso2: z.string().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  timezone: z.string().nullable(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  distanceKm: z.number().nullable(),
  networkType: z.string().nullable(),
  flags: z.record(z.string(), z.boolean()),
  /** A fixed, unflagged network point within max(50 km, radius) of the place (display only). */
  supports: z.boolean(),
})
export type IpSourceEvaluation = z.infer<typeof IpSourceEvaluationSchema>

export const IP_COUNTRY_WHY = [
  'same_country',
  'other_country',
  'other_country_unconfirmed',
  'flagged',
  'no_country',
] as const

export const IpEvaluationSchema = z.object({
  /** The observation the sources answered for (face step first). */
  step: z.string().nullable(),
  country: z.object({
    verdict: VerdictSchema,
    why: z.enum(IP_COUNTRY_WHY),
    majority: z.string().nullable(),
    /** Distinct sources reporting the majority country. */
    agreeing: z.number().int(),
  }),
  network: z.enum(NETWORK_TYPES),
  flagged: z.boolean(),
  flags: z.record(z.string(), z.boolean()),
  /** Median of the source points, and its distance to the place (km, rounded). */
  point: z.object({ lat: z.number(), lng: z.number() }).nullable(),
  distanceKm: z.number().nullable(),
  sources: z.record(z.string(), IpSourceEvaluationSchema),
})
export type IpEvaluation = z.infer<typeof IpEvaluationSchema>

export const TimezoneEvaluationSchema = z.object({
  verdict: VerdictSchema,
  why: z.enum([
    'place_zone',
    'country_zone',
    'other_zone',
    'no_device_zone',
    'generic_zone',
    'place_zone_unknown',
    'off',
  ]),
  device: z.string().nullable(),
  place: z.string().nullable(),
  cloudflare: z.string().nullable(),
})
export type TimezoneEvaluation = z.infer<typeof TimezoneEvaluationSchema>

export const LocationEvaluationSchema = z.object({
  version: z.literal(2),
  outcome: z.enum(['pass', 'review']),
  reasons: z.array(z.string()),
  confidence: z.number(),
  evaluatedAt: z.string(),
  policy: LocationPolicySchema,
  device: DeviceEvaluationSchema,
  ip: IpEvaluationSchema,
  timezone: TimezoneEvaluationSchema,
})
export type LocationEvaluation = z.infer<typeof LocationEvaluationSchema>

/** `members.location` from the identity-document spec §8.7: codes only (no coordinates, accuracy, diagnostics, IP
 * points, per-source locations or time-zone names). The full LocationEvaluation exists only in memory. */
export const LocationVerdictSchema = z.object({
  v: z.literal(1),
  outcome: z.enum(['pass', 'review']),
  level: z.enum(LOCATION_LEVELS),
  device: z.object({
    state: z.enum(['fix', 'refused', 'none', 'other_place', 'prior']),
    verdict: z.enum([...VERDICTS, 'missing']),
    why: z.enum(DEVICE_WHY),
  }),
  ip: z.enum(['match', 'mismatch', 'unknown', 'flagged']),
  timezone: z.enum(['match', 'mismatch', 'unknown']),
  reasons: z.array(z.string()),
  evaluatedAt: z.string(),
})
export type LocationVerdict = z.infer<typeof LocationVerdictSchema>

export function toLocationVerdict(e: LocationEvaluation): LocationVerdict {
  return {
    v: 1,
    outcome: e.outcome,
    level: e.policy.level,
    device: { state: e.device.state, verdict: e.device.verdict, why: e.device.why },
    ip: e.ip.flagged ? 'flagged' : e.ip.country.verdict === 'inconclusive' ? 'unknown' : e.ip.country.verdict,
    timezone: e.timezone.verdict === 'inconclusive' ? 'unknown' : e.timezone.verdict,
    reasons: [...e.reasons],
    evaluatedAt: e.evaluatedAt,
  }
}
